/**
 * Safe localStorage wrapper for Zustand persist middleware
 * Handles cases where localStorage is null (Safari private browsing, etc.)
 */

import { StateStorage } from 'zustand/middleware'

// In-memory fallback storage for when localStorage is unavailable
class MemoryStorage implements StateStorage {
  private storage = new Map<string, string>()

  getItem(key: string): string | null {
    return this.storage.get(key) ?? null
  }

  setItem(key: string, value: string): void {
    this.storage.set(key, value)
  }

  removeItem(key: string): void {
    this.storage.delete(key)
  }
}

// Create a singleton memory storage instance
const memoryStorage = new MemoryStorage()

/**
 * Check if localStorage is available and working
 */
function isLocalStorageAvailable(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    const testKey = '__storage_test__'
    const storage = window.localStorage
    
    if (!storage) {
      return false
    }

    // Test if we can actually use it
    storage.setItem(testKey, 'test')
    storage.removeItem(testKey)
    return true
  } catch {
    // localStorage exists but is not accessible (private browsing, quota exceeded, etc.)
    return false
  }
}

/**
 * Safe localStorage wrapper that falls back to memory storage
 * when localStorage is unavailable (Safari private browsing mode, etc.)
 */
class SafeLocalStorage implements StateStorage {
  private useMemoryStorage: boolean

  constructor() {
    // Determine which storage to use at construction time
    this.useMemoryStorage = !isLocalStorageAvailable()
  }

  getItem(key: string): string | null {
    try {
      if (this.useMemoryStorage) {
        return memoryStorage.getItem(key)
      }
      return window.localStorage.getItem(key)
    } catch (error) {
      console.warn(`Failed to get item from storage (key: ${key}):`, error)
      return memoryStorage.getItem(key)
    }
  }

  setItem(key: string, value: string): void {
    try {
      if (this.useMemoryStorage) {
        memoryStorage.setItem(key, value)
        return
      }
      window.localStorage.setItem(key, value)
    } catch (error) {
      console.warn(`Failed to set item in storage (key: ${key}):`, error)
      // If we're using localStorage and it fails, try falling back to memory storage
      if (!this.useMemoryStorage) {
        console.warn('Falling back to memory storage')
        this.useMemoryStorage = true
        try {
          memoryStorage.setItem(key, value)
        } catch (fallbackError) {
          console.error('Failed to set item even in memory storage:', fallbackError)
        }
      }
    }
  }

  removeItem(key: string): void {
    try {
      if (this.useMemoryStorage) {
        memoryStorage.removeItem(key)
        return
      }
      window.localStorage.removeItem(key)
    } catch (error) {
      console.warn(`Failed to remove item from storage (key: ${key}):`, error)
      if (!this.useMemoryStorage) {
        memoryStorage.removeItem(key)
      }
    }
  }
}

/**
 * Create a safe storage instance for Zustand persist
 * This can be used directly with createJSONStorage
 */
export function createSafeStorage(): StateStorage {
  if (typeof window === 'undefined') {
    // Server-side: return memory storage
    return memoryStorage
  }

  return new SafeLocalStorage()
}

/**
 * Check if sessionStorage is available and working
 */
function isSessionStorageAvailable(): boolean {
  if (typeof window === 'undefined') {
    return false
  }

  try {
    const testKey = '__session_storage_test__'
    const storage = window.sessionStorage
    
    if (!storage) {
      return false
    }

    // Test if we can actually use it
    storage.setItem(testKey, 'test')
    storage.removeItem(testKey)
    return true
  } catch {
    // sessionStorage exists but is not accessible
    return false
  }
}

/**
 * Helper to safely access localStorage directly (not for Zustand)
 */
export const safeLocalStorage = {
  getItem: (key: string): string | null => {
    if (!isLocalStorageAvailable()) {
      return memoryStorage.getItem(key)
    }
    try {
      return window.localStorage.getItem(key)
    } catch {
      return memoryStorage.getItem(key)
    }
  },

  setItem: (key: string, value: string): void => {
    if (!isLocalStorageAvailable()) {
      memoryStorage.setItem(key, value)
      return
    }
    try {
      window.localStorage.setItem(key, value)
    } catch {
      memoryStorage.setItem(key, value)
    }
  },

  removeItem: (key: string): void => {
    if (!isLocalStorageAvailable()) {
      memoryStorage.removeItem(key)
      return
    }
    try {
      window.localStorage.removeItem(key)
    } catch {
      memoryStorage.removeItem(key)
    }
  },
}

/**
 * Helper to safely access sessionStorage directly
 */
export const safeSessionStorage = {
  getItem: (key: string): string | null => {
    if (!isSessionStorageAvailable()) {
      return memoryStorage.getItem(key)
    }
    try {
      return window.sessionStorage.getItem(key)
    } catch {
      return memoryStorage.getItem(key)
    }
  },

  setItem: (key: string, value: string): void => {
    if (!isSessionStorageAvailable()) {
      memoryStorage.setItem(key, value)
      return
    }
    try {
      window.sessionStorage.setItem(key, value)
    } catch {
      memoryStorage.setItem(key, value)
    }
  },

  removeItem: (key: string): void => {
    if (!isSessionStorageAvailable()) {
      memoryStorage.removeItem(key)
      return
    }
    try {
      window.sessionStorage.removeItem(key)
    } catch {
      memoryStorage.removeItem(key)
    }
  },
}

